/**
 * @file AttachmentSyncNudge.tsx
 * @description One line on a large file's row, suggesting Syncretic File Sync.
 * Consumer: `AttachmentList`, which renders it on at most one row and never in
 * the PDF export portal.
 *
 * Every condition that silences it lives in `resolveAttachmentSyncNudge`; this
 * component supplies the four readings and the two actions. It is a nudge and
 * never a banner: it sits on the row it is about, and dismissing it is
 * permanent.
 *
 * `fileSyncNudgeDismissedAt` is read straight off the `users/{uid}` store rather
 * than through `UserContext`. `UserProvider` re-exports a fixed list of settings
 * keys and this one is not on it; adding it would re-render the top of the app
 * on a write only this line cares about. The write is a dotted field path so it
 * cannot clobber a neighbouring setting, and it is undebounced because the
 * reader is watching the line disappear.
 *
 * That store is connected here rather than borrowed. `createDocumentStore` only
 * consults the registry — `connect` is what registers a path — so relying on
 * `UserProvider` winning the race would leave an orphan whose `state` never
 * updates and whose `update` throws. The refcount makes this a second holder of
 * the one listener, not a second listener.
 *
 * An unconfigured download URL returns before the sentence is resolved, so the
 * `href` below is a string by the time it is read and neither guard duplicates
 * the other. `resolveAttachmentSyncNudge` still takes the URL, because it is the
 * one place every reason for silence is gathered.
 */

import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Typography } from 'antd';
import { serverTimestamp } from 'firebase/firestore';
import { useSnapshot } from 'valtio';

import type { User } from '#types';
import FlexBox from '../../atoms/FlexBox';
import { MessageContext } from '../../../providers/MessageContext';
import { UserContext } from '../../../providers/UserContext';
import { createDocumentStore, type DocumentStore } from '../../../store';
import { useDesktopPins } from '../../../use/desktopSync/useDesktopPins';
import { resolveAttachmentSyncNudge } from './resolveAttachmentSyncNudge';
import { useDesktopSyncDownloadUrl } from './useDesktopSyncDownloadUrl';

const { Link, Text } = Typography;

const inactiveStore = createDocumentStore<User>('users/__inactive__');

interface AttachmentSyncNudgeProps {
    /** The row this line belongs to; names the dismiss control. */
    fileUid: string;
    fileName: string;
    /** The row's current size, which is what puts it over the advisory threshold. */
    sizeBytes: number | undefined;
}

export const AttachmentSyncNudge: FC<AttachmentSyncNudgeProps> = ({
    fileUid,
    fileName,
    sizeBytes,
}) => {
    const { user } = useContext(UserContext);
    const { handleFirestoreWriteError } = useContext(MessageContext);
    const { pins, loading } = useDesktopPins();
    const downloadUrl = useDesktopSyncDownloadUrl();
    const [dismissing, setDismissing] = useState(false);

    const uid = user?.uid;
    const store = useMemo<DocumentStore<User>>(
        () => uid ? createDocumentStore<User>(`users/${uid}`) : inactiveStore,
        [uid],
    );
    useEffect(() => {
        if (!uid) return;
        store.connect();
        return () => store.disconnect();
    }, [uid, store]);

    const userSnap = useSnapshot(store.state);
    const dismissed = userSnap.data?.settings?.fileSyncNudgeDismissedAt !== undefined;

    const dismiss = useCallback(async () => {
        if (!uid) return;
        setDismissing(true);
        try {
            await store.update(
                { 'settings.fileSyncNudgeDismissedAt': serverTimestamp() },
                { debounce: false },
            );
        } catch (error) {
            handleFirestoreWriteError(error, '[AttachmentSyncNudge] dismiss');
        } finally {
            setDismissing(false);
        }
    }, [uid, store, handleFirestoreWriteError]);

    if (downloadUrl === null) {
        return null;
    }

    const sentence = resolveAttachmentSyncNudge({
        sizeBytes,
        hasAnyPin: Object.keys(pins).length > 0,
        loading,
        dismissed,
        downloadUrl,
    });

    if (sentence === null) {
        return null;
    }

    return (
        <FlexBox
            wrap
            gap={5}
            style={{ padding: 5, alignItems: 'center' }}
            id={`attachment-sync-nudge-${fileUid}`}
        >
            <Text type="secondary">{sentence}</Text>
            <Link
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                id={`attachment-sync-nudge-link-${fileUid}`}
                aria-label={`Get Syncretic File Sync, suggested for ${fileName}`}
            >
                Get Syncretic File Sync
            </Link>
            <Button
                size="small"
                variant="text"
                color="default"
                loading={dismissing}
                onClick={() => void dismiss()}
                id={`attachment-sync-nudge-dismiss-${fileUid}`}
                aria-label={`Dismiss the Syncretic File Sync suggestion for ${fileName}`}
            >
                Dismiss
            </Button>
        </FlexBox>
    );
};
